import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Trash2, SquarePen, Sparkles } from 'lucide-react';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import { showToast } from '../../../utils/toastManager';
import MonthPicker from '../../molecules/profile/components/MonthPicker';
import '../../../styles/resume-editor/edit-experience-form.css';

const SHOW_PROJECTS_AI_TOOLS = false;

interface Project {
  uid: string;
  title: string;
  headline: string;
  link?: string;
  linkAlias?: string;
  description?: string;
  releaseDate?: string | null;
  yearOnly?: boolean;
  hide?: boolean;
  achievements?: string[];
}

interface EditProjectFormProps {
  project: Project;
  onSave: (data: Project) => void;
  onCancel: () => void;
  onDataChange?: (data: Project) => void;
  cvUid?: string;
}

export const EditProjectForm: React.FC<EditProjectFormProps> = ({
  project,
  onSave,
  onCancel,
  onDataChange,
  cvUid,
}) => {
  const [title, setTitle] = useState('');
  const [headline, setHeadline] = useState('');
  const [link, setLink] = useState('');
  const [linkAlias, setLinkAlias] = useState('');
  const [description, setDescription] = useState('');
  const [releaseDate, setReleaseDate] = useState<Date | null>(null);
  const [showReleaseCalendar, setShowReleaseCalendar] = useState(false);
  const [yearOnly, setYearOnly] = useState(false);
  const [hide, setHide] = useState(false);
  const [achievements, setAchievements] = useState<string[]>([]);
  const [editingAchievementIndex, setEditingAchievementIndex] = useState<number | null>(null);
  const [editingAchievementText, setEditingAchievementText] = useState<string>('');
  const [hoveredAchievementIndex, setHoveredAchievementIndex] = useState<number | null>(null);
  const [hoveredActionIndex, setHoveredActionIndex] = useState<{ achievementIndex: number; actionIndex: number } | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadedProjectUidRef = useRef<string | null>(null);
  const prevDataRef = useRef<string>('');
  const onDataChangeRef = useRef(onDataChange);

  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);
  
  useEffect(() => {
    if (project && project.uid !== loadedProjectUidRef.current) {
      loadedProjectUidRef.current = project.uid;
      setTitle(project.title || '');
      setHeadline(project.headline || '');
      setLink(project.link || '');
      setLinkAlias(project.linkAlias || '');
      setDescription(project.description || '');
      setReleaseDate(project.releaseDate ? new Date(project.releaseDate) : null);
      setYearOnly(project.yearOnly || false);
      setHide(project.hide || false);
      setAchievements(project.achievements || []);
      
      if (onDataChangeRef.current) {
        const initialProject: Project = {
          ...project,
          title: project.title || '',
          headline: project.headline || '',
          link: project.link || '',
          linkAlias: project.linkAlias || '',
          description: project.description || '',
          releaseDate: project.releaseDate || null,
          yearOnly: project.yearOnly || false,
          hide: project.hide || false,
          achievements: project.achievements || []
        };
        onDataChangeRef.current(initialProject);
      }
    }
  }, [project?.uid]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ select date';
    if (yearOnly) {
      return date.getFullYear().toString();
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} - ${year}`;
  };

  const handleAddAchievement = () => {
    const newIndex = achievements.length;
    setAchievements([...achievements, '']);
    setEditingAchievementIndex(newIndex);
    setEditingAchievementText('');
  };

  const handleRemoveAchievement = (index: number) => {
    setAchievements(achievements.filter((_, i) => i !== index));
  };

  const handleEditAchievement = (index: number) => {
    setEditingAchievementIndex(index);
    setEditingAchievementText(achievements[index]);
  };

  const handleSaveAchievementEdit = (index: number) => {
    const trimmedText = editingAchievementText.trim();
    if (trimmedText) {
      const newAchievements = [...achievements];
      newAchievements[index] = trimmedText;
      setAchievements(newAchievements);
    } else {
      setAchievements(achievements.filter((_, i) => i !== index));
    }
    setEditingAchievementIndex(null);
    setEditingAchievementText('');
  };

  const handleCancelAchievementEdit = () => {
    if (editingAchievementIndex !== null && achievements) {
      const achievement = achievements[editingAchievementIndex];
      if (achievement === '') {
        setAchievements(achievements.filter((_, i) => i !== editingAchievementIndex));
      }
    }
    setEditingAchievementIndex(null);
    setEditingAchievementText('');
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', '');
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newAchievements = [...achievements];
    const [removed] = newAchievements.splice(draggedIndex, 1);
    newAchievements.splice(dropIndex, 0, removed);
    setAchievements(newAchievements);

    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  useEffect(() => {
    if (editingAchievementIndex !== null && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [editingAchievementIndex]);

  const createUpdatedProject = (): Project => {
    const updated: Project = {
      ...project,
      title: title.trim(),
      headline: headline.trim(),
      link: link.trim() || undefined,
      linkAlias: linkAlias.trim() || undefined,
      description: description.trim() || undefined,
      releaseDate: releaseDate ? releaseDate.toISOString() : null,
      yearOnly,
      hide,
      achievements: achievements.length > 0 ? achievements : undefined,
    };
    return updated;
  };

  const updatePreviewData = () => {
    if (onDataChangeRef.current) {
      const updated = createUpdatedProject();
      const currentData = JSON.stringify({
        title,
        headline,
        link,
        linkAlias,
        description,
        releaseDate: releaseDate?.toISOString(),
        yearOnly,
        hide,
        achievements
      });
      
      if (currentData !== prevDataRef.current) {
        prevDataRef.current = currentData;
        onDataChangeRef.current(updated);
      }
    }
  };

  useEffect(() => {
    const shouldUpdate = project?.uid === 'new' || project?.uid === loadedProjectUidRef.current;
    if ((title !== '' || headline !== '' || project) && shouldUpdate) {
      updatePreviewData();
    }
  }, [title, headline, link, linkAlias, description, releaseDate, yearOnly, hide, achievements, project?.uid]);

  const handleSave = () => {
    if (!title.trim() || !headline.trim()) {
      return;
    }

    const updatedProject = createUpdatedProject();
    onSave(updatedProject);
  };

  return (
    <div className="edit-experience-form">
      <div className="edit-experience-form-content">
      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">
          <span>Title</span>
          <span className="edit-experience-form-label-required">Required</span>
        </label>
        <input
          type="text"
          className="edit-experience-form-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">
          <span>Headline</span>
          <span className="edit-experience-form-label-required">Required</span>
        </label>
        <input
          type="text"
          className="edit-experience-form-input"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
        />
      </div>

      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">Link</label>
        <input
          type="url"
          className="edit-experience-form-input"
          placeholder="https://example.com"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
      </div>

      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">Link Alias</label>
        <input
          type="text"
          className="edit-experience-form-input"
          placeholder="https://github.com/"
          value={linkAlias}
          onChange={(e) => setLinkAlias(e.target.value)}
        />
      </div>

      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">Description</label>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <textarea
            className="edit-experience-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter project description..."
            rows={3}
            style={{ width: '100%' }}
          />
          {SHOW_PROJECTS_AI_TOOLS && project.uid !== 'new' && (
            <button
              type="button"
              className="edit-experience-form-ai-suggestions-btn"
              onClick={async () => {
                if (cvUid) {
                  try {
                    const payload: any = {
                      cv_uid: cvUid,
                      entity_type: 'projects_description',
                      entity_uid: project.uid,
                      improvement_type: description && description.trim() !== '' ? 'current' : 'new',
                    };
                    
                    if (description && description.trim() !== '') {
                      payload.old_value = { value: [description] };
                    }
                    
                    await api.post(API_ENDPOINTS.aiCv.createTask, payload);
                    if ((window as any).__refreshAITasks) {
                      (window as any).__refreshAITasks();
                    }
                  } catch (error: any) {
                    console.error('Failed to create AI CV task for project description:', error);
                    const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                    showToast(errorMessage, 'error');
                  }
                }
              }}
            >
              <Sparkles size={14} />
              <span>Generate with AI</span>
            </button>
          )}
        </div>
      </div>

      <div className="edit-experience-form-field">
        <div className="edit-experience-form-dates-row">
          <div className="edit-experience-form-date-wrapper">
            <label className="edit-experience-form-label">Release Date</label>
            <button
              type="button"
              className="edit-experience-form-date-btn"
              onClick={() => {
                setShowReleaseCalendar(!showReleaseCalendar);
              }}
              disabled={hide}
            >
              {formatDate(releaseDate)}
            </button>
            {showReleaseCalendar && (
              <div className="edit-experience-form-month-picker-wrapper">
                <MonthPicker
                  selectedDate={releaseDate}
                  onSelect={(date) => {
                    setReleaseDate(date);
                    setShowReleaseCalendar(false);
                  }}
                  onClose={() => setShowReleaseCalendar(false)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="edit-experience-form-date-toggles">
          <label className="edit-experience-form-toggle-label">
            <span>Year only</span>
            <div
              className={`edit-experience-form-toggle-switch ${yearOnly ? 'edit-experience-form-toggle-switch--active' : ''}`}
              onClick={() => {
                setYearOnly(!yearOnly);
                updatePreviewData();
              }}
            >
              <div className="edit-experience-form-toggle-switch-thumb" />
            </div>
          </label>
          <label className="edit-experience-form-toggle-label">
            <span>Hide</span>
            <div
              className={`edit-experience-form-toggle-switch ${hide ? 'edit-experience-form-toggle-switch--active' : ''}`}
              onClick={() => {
                setHide(!hide);
                updatePreviewData();
              }}
            >
              <div className="edit-experience-form-toggle-switch-thumb" />
            </div>
          </label>
        </div>
      </div>

      <div className="edit-experience-form-field">
        <label className="edit-experience-form-label">Achievements</label>
        <div className="edit-experience-form-achievements-list">
          {achievements.length === 0 ? (
            <div className="edit-experience-form-achievements-empty">
              No achievements added...
            </div>
          ) : (
            achievements.map((achievement, index) => (
            editingAchievementIndex === index ? (
              <div key={index} className="edit-experience-form-achievement-tile edit-experience-form-achievement-tile--editing">
                <textarea
                  ref={textareaRef}
                  className="edit-experience-form-achievement-textarea"
                  value={editingAchievementText}
                  onChange={(e) => setEditingAchievementText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveAchievementEdit(index);
                    }
                    if (e.key === 'Escape') {
                      handleCancelAchievementEdit();
                    }
                  }}
                  rows={2}
                />
                <div className="edit-experience-form-achievement-edit-actions">
                  <button
                    type="button"
                    className="edit-experience-form-achievement-save-btn"
                    onClick={() => handleSaveAchievementEdit(index)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="edit-experience-form-achievement-cancel-btn"
                    onClick={handleCancelAchievementEdit}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div 
                key={index} 
                className={`edit-experience-form-achievement-tile ${
                  draggedIndex === index ? 'edit-experience-form-achievement-tile--dragging' : ''
                } ${
                  dragOverIndex === index ? 'edit-experience-form-achievement-tile--drag-over' : ''
                }`}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                onMouseEnter={() => setHoveredAchievementIndex(index)}
                onMouseLeave={() => setHoveredAchievementIndex(null)}
                onClick={() => handleEditAchievement(index)}
              >
                <span className="edit-experience-form-achievement-text">{achievement}</span>
                {hoveredAchievementIndex === index && (
                  <div className="edit-experience-form-achievement-actions">
                    <div
                      className="edit-experience-form-achievement-action-wrapper"
                      onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 0 })}
                      onMouseLeave={() => setHoveredActionIndex(null)}
                    >
                    <button
                      type="button"
                      className="edit-experience-form-achievement-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAchievement(index);
                      }}
                    >
                      <SquarePen size={14} />
                    </button>
                      {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 0 && (
                        <span className="edit-experience-form-achievement-tooltip">Edit</span>
                      )}
                    </div>
                    {SHOW_PROJECTS_AI_TOOLS && (
                    <div
                      className="edit-experience-form-achievement-action-wrapper"
                      onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 1 })}
                      onMouseLeave={() => setHoveredActionIndex(null)}
                    >
                      <button
                        type="button"
                        className="edit-experience-form-achievement-action"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (cvUid) {
                            try {
                              await api.post(API_ENDPOINTS.aiCv.createTask, {
                                cv_uid: cvUid,
                                entity_type: 'projects_achievement',
                                entity_uid: project.uid,
                                improvement_type: 'current',
                                old_value: { value: [achievement] },
                              });
                              if ((window as any).__refreshAITasks) {
                                (window as any).__refreshAITasks();
                              }
                            } catch (error: any) {
                              console.error('Failed to create AI CV task for achievement:', error);
                              const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                              showToast(errorMessage, 'error');
                            }
                          }
                        }}
                      >
                        <Sparkles size={14} />
                      </button>
                      {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 1 && (
                        <span className="edit-experience-form-achievement-tooltip">Rewrite with AI</span>
                      )}
                    </div>
                    )}
                    <div
                      className="edit-experience-form-achievement-action-wrapper"
                      onMouseEnter={() => setHoveredActionIndex({ achievementIndex: index, actionIndex: 2 })}
                      onMouseLeave={() => setHoveredActionIndex(null)}
                    >
                      <button
                        type="button"
                        className="edit-experience-form-achievement-action edit-experience-form-achievement-action--delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveAchievement(index);
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                      {hoveredActionIndex?.achievementIndex === index && hoveredActionIndex?.actionIndex === 2 && (
                        <span className="edit-experience-form-achievement-tooltip">Remove</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          )))}
          <div className="edit-experience-form-achievement-buttons">
            <button
              type="button"
              className="edit-experience-form-add-achievement-btn"
              onClick={handleAddAchievement}
            >
              + Achievement
            </button>
            {SHOW_PROJECTS_AI_TOOLS && project.uid !== 'new' && (
              <button
                type="button"
                className="edit-experience-form-ai-suggestions-btn"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (cvUid) {
                    try {
                      await api.post(API_ENDPOINTS.aiCv.createTask, {
                        cv_uid: cvUid,
                        entity_type: 'projects_block',
                        entity_uid: project.uid,
                        improvement_type: 'new',
                      });
                      if ((window as any).__refreshAITasks) {
                        (window as any).__refreshAITasks();
                      }
                    } catch (error: any) {
                      console.error('Failed to create AI CV task for project:', error);
                      const errorMessage = error?.data?.detail || error?.message || 'Failed to create AI task. Please try again.';
                      showToast(errorMessage, 'error');
                    }
                  }
                }}
              >
                <Sparkles size={14} /> AI Suggestions
              </button>
            )}
          </div>
        </div>
      </div>
      </div>

      <div className="edit-experience-form-footer">
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--update"
          onClick={handleSave}
          disabled={!title.trim() || !headline.trim()}
        >
          {project.uid === 'new' ? 'Add' : 'Update'}
        </button>
      </div>
    </div>
  );
};

